import { css } from '@emotion/react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router';

import { formatSyncLogAsText } from '@kaiizen/kai-presenters';
import {
  getAge,
  ProgressDetails,
  usePhotoGallery,
  useScanSynchronizer,
  useSyncScanProgress,
} from '@kaiizen/kai-pro-api-react-state';
import { usePersistentCache } from '@kaiizen/react-persistent-cache';

import { useAuth, useConfirmationModal } from '@/Client/State';
import { LAYOUT2, TYPOGRAPHY2 } from '@/Client/Styles';
import { pxToRem } from '@/Client/Styles/pxToRem';
import { Button, CustomToggle } from '@/Client/Widgets';

const MIN_ACTIVE_PROGRESS_AGE_MS = 1000;

const MODAL_OVERLAY_STYLE = css`
  label: MODAL_OVERLAY_STYLE;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 2000;
  padding: 20px;
`;

const MODAL_CONTENT_STYLE = css`
  label: MODAL_CONTENT_STYLE;
  background: var(--background);
  padding: ${LAYOUT2.spacing2}px;
  max-width: 900px;
  width: 100%;
  height: 80vh;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1), 0 10px 20px rgba(0, 0, 0, 0.15);
`;

const MODAL_SCROLLABLE_STYLE = css`
  label: MODAL_SCROLLABLE_STYLE;
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow-y: auto;
  gap: ${LAYOUT2.spacing2}px;
  background: var(--background1);
`;

const MODAL_TITLE_STYLE = css`
  label: MODAL_TITLE_STYLE;
  ${TYPOGRAPHY2.largeBold}
  color: var(--text);
  text-align: center;
`;

const MODAL_HEADER_ROW_STYLE = css`
  label: MODAL_HEADER_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${LAYOUT2.spacing2}px;
`;

const HEADER_ACTIONS_STYLE = css`
  label: HEADER_ACTIONS_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing2}px;
`;

const SYNC_TOGGLE_ROW_STYLE = css`
  label: SYNC_TOGGLE_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing1}px;
`;

const SYNC_TOGGLE_LABEL_STYLE = css`
  label: SYNC_TOGGLE_LABEL_STYLE;
  ${TYPOGRAPHY2.xSmallBold};
  color: var(--text);
  user-select: none;
`;

const MODAL_SECTION_STYLE = css`
  label: MODAL_SECTION_STYLE;
  background: var(--background2);
  padding: ${LAYOUT2.spacing2}px;
`;

const MODAL_SECTION_TITLE_STYLE = css`
  label: MODAL_SECTION_TITLE_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
  margin-bottom: ${LAYOUT2.spacing2}px;
`;

const MODAL_SECTION_HEADER_ROW_STYLE = css`
  label: MODAL_SECTION_HEADER_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${LAYOUT2.spacing2}px;

  & > div:first-of-type {
    margin-bottom: 0;
  }
`;

const MODAL_STAT_ROW_STYLE = css`
  label: MODAL_STAT_ROW_STYLE;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: ${LAYOUT2.spacing1}px 0;
  border-bottom: 1px solid var(--border);

  &:last-child {
    border-bottom: none;
  }
`;

const MODAL_STAT_LABEL_STYLE = css`
  label: MODAL_STAT_LABEL_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text2);
`;

const MODAL_STAT_VALUE_STYLE = css`
  label: MODAL_STAT_VALUE_STYLE;
  ${TYPOGRAPHY2.mediumBold}
  color: var(--text);
`;

const LOG_TEXTAREA_STYLE = css`
  label: LOG_TEXTAREA_STYLE;
  width: 100%;
  font-family: 'Monaco', 'Menlo', 'Ubuntu Mono', 'Consolas', 'Courier New', monospace;
  font-size: ${pxToRem(TYPOGRAPHY2.fontSize.xSmall)} !important;
  line-height: 1.4;
  padding: ${LAYOUT2.spacing2}px;
  background: var(--background);
  color: var(--text);
  border: 1px solid var(--border);
  white-space: pre;
  overflow-x: scroll;
`;

const COPY_BUTTON_STYLE = css`
  label: COPY_BUTTON_STYLE;
  ${TYPOGRAPHY2.small}
  padding: ${LAYOUT2.spacing1}px ${LAYOUT2.spacing2}px;
  background: var(--background);
  color: var(--text);
  border: 1px solid var(--border);
  cursor: pointer;
  white-space: nowrap;

  &:hover {
    background: var(--background2);
  }

  &:active {
    background: var(--border);
  }
`;

const MODAL_FOOTER_STYLE = css`
  label: MODAL_FOOTER_STYLE;
  flex-shrink: 0;
  padding-top: ${LAYOUT2.spacing2}px;
  border-top: 1px solid var(--border);
`;

const MODAL_BUTTONS_STYLE = css`
  label: MODAL_BUTTONS_STYLE;
  display: flex;
  gap: ${LAYOUT2.spacing2}px;
  justify-content: space-between;
`;

const MODAL_BUTTON_STYLE = css`
  label: MODAL_BUTTON_STYLE;
  flex: 1;
`;

const MODAL_NO_ERRORS_STYLE = css`
  label: MODAL_NO_ERRORS_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--textGreen);
  text-align: center;
  padding: ${LAYOUT2.spacing2}px;
`;

const MODAL_NO_STATS_STYLE = css`
  label: MODAL_NO_STATS_STYLE;
  ${TYPOGRAPHY2.medium}
  color: var(--text2);
  text-align: center;
  padding: ${LAYOUT2.spacing2}px;
`;

const ACTIVE_PROGRESS_ROW_STYLE = css`
  label: ACTIVE_PROGRESS_ROW_STYLE;
  display: flex;
  align-items: center;
  gap: ${LAYOUT2.spacing2}px;
  margin-bottom: ${LAYOUT2.spacing1}px;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ACTIVE_PROGRESS_LABEL_STYLE = css`
  label: ACTIVE_PROGRESS_LABEL_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text);
  flex: 0 1 40%;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const ACTIVE_PROGRESS_TRACK_STYLE = css`
  label: ACTIVE_PROGRESS_TRACK_STYLE;
  flex: 1;
  min-width: 0;
  height: 10px;
  background: var(--border);
  overflow: hidden;
`;

const ACTIVE_PROGRESS_FILL_STYLE = css`
  label: ACTIVE_PROGRESS_FILL_STYLE;
  height: 100%;
  background: var(--textBlue);
  transition: width 0.2s ease-out;
`;

const ACTIVE_PROGRESS_PERCENT_STYLE = css`
  label: ACTIVE_PROGRESS_PERCENT_STYLE;
  ${TYPOGRAPHY2.small}
  color: var(--text);
  width: ${LAYOUT2.spacing * 5}px;
  text-align: right;
`;

const ActiveProgressRow = ({ details }: { details: ProgressDetails }) => (
  <div css={ACTIVE_PROGRESS_ROW_STYLE}>
    <span css={ACTIVE_PROGRESS_LABEL_STYLE} title={details.label}>{details.label}</span>
    <div css={ACTIVE_PROGRESS_TRACK_STYLE}>
      <div css={ACTIVE_PROGRESS_FILL_STYLE} style={{ width: `${details.progress * 100}%` }} />
    </div>
    <div css={ACTIVE_PROGRESS_PERCENT_STYLE}>
      {Math.round(details.progress * 100)}%
    </div>
  </div>
);

const copyToClipboard = async (text: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(text);
  } catch (error) {
    console.log({ clipboardError: error });
  }
};

export const SyncManagementModal = ({ visible, onClose }: {
  visible: boolean;
  onClose: () => void;
}) => {
  const { stats, syncLog, deleteAndLoseAllUnsyncedData, clearSyncStats, activeProgressTracker, isPaused, togglePaused } = useScanSynchronizer();
  const { isSuperAdmin } = useAuth();
  const { scanDataSyncStatus, photosSyncStatus, notesSyncStatus, syncStatus } = useSyncScanProgress();
  const confirm = useConfirmationModal();
  const persistentCache = usePersistentCache();
  const photoGallery = usePhotoGallery();
  const navigate = useNavigate();

  const hardErrors = stats.errors.filter(error => error.hardError);
  const softErrors = stats.errors.filter(error => !error.hardError);

  const totalPending = syncStatus.pending;

  const handleDeleteAllSyncData = () => {
    confirm({
      title: 'Delete All Sync Data',
      message:
        'Are you sure you want to delete all sync data? In order to ensure consistency we will also erase all cached and offline data. Fresh data will be downloaded from the server.',
      confirmLabel: 'Delete',
      cancelLabel: 'Cancel',
      onConfirm: async () => {
        await deleteAndLoseAllUnsyncedData();
        persistentCache.purgeAllEntries();
        photoGallery.dangerouslyEraseUnsyncedPhotos();
        onClose();
        navigate('/');
      },
    });
  };

  if (!visible) return null;

  const errorEntries = stats.errors.map(error => ({ type: 'error' as const, ...error }));
  const logEntries = syncLog.filter(log => log.level !== 'debug').map(log => ({ type: 'log' as const, ...log }));
  const displayedLogEntries = [...errorEntries, ...logEntries]
    .sort((a, b) => b.date.getTime() - a.date.getTime());

  const statRows = [
    { label: 'Pending Scan Data', value: scanDataSyncStatus.pending },
    { label: 'Pending Photos', value: photosSyncStatus.pending },
    { label: 'Pending Notes', value: notesSyncStatus.pending },
    { label: 'Scan Data Synced', value: stats.scanData },
    { label: 'Photos Synced', value: stats.scanPhoto },
    { label: 'Scan Notes Synced', value: stats.scanNote },
    { label: 'Total Failures', value: hardErrors.length },
    { label: 'Total Retries', value: softErrors.length },
  ].filter(row => row.value !== 0);

  const activeProgressEntries = Object.entries(activeProgressTracker).sort(
    ([, a], [, b]) => a.firstProgressAt.getTime() - b.firstProgressAt.getTime(),
  );

  return createPortal(
    <div css={MODAL_OVERLAY_STYLE} onClick={onClose}>
      <div css={MODAL_CONTENT_STYLE} onClick={event => event.stopPropagation()}>
        <div css={MODAL_HEADER_ROW_STYLE}>
          <div css={MODAL_TITLE_STYLE}>Sync Management</div>
          <div css={HEADER_ACTIONS_STYLE}>
            {isSuperAdmin && (
              <div css={SYNC_TOGGLE_ROW_STYLE}>
                <span css={SYNC_TOGGLE_LABEL_STYLE}>
                  {isPaused ? 'SYNCING PAUSED' : 'Syncing enabled'} (super admin only)
                </span>
                <CustomToggle
                  isOn={!isPaused}
                  onToggle={togglePaused}
                  onLabel=''
                  offLabel=''
                />
              </div>
            )}
            <button css={COPY_BUTTON_STYLE} onClick={clearSyncStats}>
              Reset Stats
            </button>
          </div>
        </div>
        <div css={MODAL_SCROLLABLE_STYLE}>
          <div css={MODAL_SECTION_STYLE}>
            <div css={MODAL_SECTION_TITLE_STYLE}>Sync Statistics</div>
            {statRows.length > 0
              ? (
                statRows.map(row => (
                  <div key={row.label} css={MODAL_STAT_ROW_STYLE}>
                    <span css={MODAL_STAT_LABEL_STYLE}>{row.label}:</span>
                    <span css={MODAL_STAT_VALUE_STYLE}>{row.value}</span>
                  </div>
                ))
              )
              : <div css={MODAL_NO_STATS_STYLE}>No sync statistics yet</div>}
          </div>

          {activeProgressEntries.length > 0 && (
            <div css={MODAL_SECTION_STYLE}>
              <div css={MODAL_SECTION_TITLE_STYLE}>Sync progress</div>
              {activeProgressEntries
                .filter(([, details]) => getAge(details.firstProgressAt) > MIN_ACTIVE_PROGRESS_AGE_MS)
                .map(([progressId, details]) => <ActiveProgressRow key={progressId} details={details} />)}
            </div>
          )}

          <div css={MODAL_SECTION_STYLE}>
            <div css={MODAL_SECTION_HEADER_ROW_STYLE}>
              <div css={MODAL_SECTION_TITLE_STYLE}>Sync Log</div>
              {displayedLogEntries.length > 0 && (
                <button
                  css={COPY_BUTTON_STYLE}
                  onClick={() => copyToClipboard(formatSyncLogAsText(displayedLogEntries))}
                >
                  Copy Log
                </button>
              )}
            </div>

            {displayedLogEntries.length > 0
              ? (
                <div
                  css={LOG_TEXTAREA_STYLE}
                >
                  {formatSyncLogAsText(displayedLogEntries)}
                </div>
              )
              : (
                <div css={MODAL_NO_ERRORS_STYLE}>
                  No sync log entries yet
                </div>
              )}
          </div>
        </div>

        <div css={MODAL_FOOTER_STYLE}>
          <div css={MODAL_BUTTONS_STYLE}>
            <Button
              onClick={handleDeleteAllSyncData}
              css={MODAL_BUTTON_STYLE}
              disabled={totalPending === 0}
            >
              Delete All Sync Data
            </Button>
            <Button onClick={onClose} css={MODAL_BUTTON_STYLE}>
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body,
  );
};
